"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useRef,
} from "react";
import { AuthApiClient } from "@/lib/api/clients/auth-api-client";
import { UnifiedCacheManager } from "@/lib/utils/cache-manager";

interface PublicConfig {
  google_enabled: boolean;
  google_client_id: string | null;
}

interface ConfigContextType extends PublicConfig {
  isLoadingConfig: boolean;
}

// Create context with a default value to prevent null issues
const defaultContextValue: ConfigContextType = {
  google_enabled: false,
  google_client_id: null,
  isLoadingConfig: true,
};

const ConfigContext = createContext<ConfigContextType>(defaultContextValue);

// Global cache and request management using unified cache manager
class GlobalConfigCache {
  private static readonly CACHE_KEY = "public-config";
  private static readonly CONFIG: any = {
    ttl: 5 * 60 * 1000, // 5 minutes
    storage: "sessionStorage" as const,
    version: "1.0.0",
  };

  static get(): PublicConfig | null {
    return UnifiedCacheManager.get<PublicConfig>(this.CACHE_KEY, this.CONFIG);
  }

  static set(config: PublicConfig): void {
    UnifiedCacheManager.set(this.CACHE_KEY, config, this.CONFIG);
  }

  static async load(): Promise<PublicConfig> {
    return UnifiedCacheManager.load(
      this.CACHE_KEY,
      async () => {
        const authApiClient = new AuthApiClient();
        const response = await authApiClient.getPublicConfig();
        return {
          google_enabled: Boolean(response.google_enabled),
          google_client_id: response.google_client_id || null,
        };
      },
      this.CONFIG
    );
  }

  static clear(): void {
    UnifiedCacheManager.delete(this.CACHE_KEY, this.CONFIG);
  }
}

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<PublicConfig>({
    google_enabled: false,
    google_client_id: null,
  });
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [mounted, setMounted] = useState(false);

  // Handle hydration by waiting for client-side mount
  useEffect(() => {
    setMounted(true);
  }, []);

  // Load config using global cache
  useEffect(() => {
    if (!mounted) return;

    const loadConfig = async () => {
      setIsLoadingConfig(true);
      try {
        const configData = await GlobalConfigCache.load();
        setConfig(configData);
      } catch (error) {
        // Only log error if it's not a network/auth issue
        if (
          error instanceof Error &&
          !error.message.includes("401") &&
          !error.message.includes("session")
        ) {
          console.error("Failed to fetch public config:", error);
        }
        // Set safe defaults
        const fallbackConfig = {
          google_enabled: false,
          google_client_id: null,
        };
        setConfig(fallbackConfig);
      } finally {
        setIsLoadingConfig(false);
      }
    };

    loadConfig();
  }, [mounted]);

  // Memoize the context value to prevent infinite re-renders
  const contextValue = useMemo(() => {
    return {
      ...config,
      isLoadingConfig: !mounted || isLoadingConfig,
    };
  }, [
    config.google_enabled,
    config.google_client_id,
    isLoadingConfig,
    mounted,
  ]);

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  return context;
};

// Export the global cache for use by other services
export { GlobalConfigCache };
